import { z } from 'zod';

export const COMMENT_VISIBILITIES = ['public', 'internal'] as const;
export const CommentVisibilitySchema = z.enum(COMMENT_VISIBILITIES);
export type CommentVisibility = z.infer<typeof CommentVisibilitySchema>;

/** Same ceiling as `ticket.description` (Data API 64 KB field cap, ADR-0015 §3). */
export const COMMENT_BODY_MAX = 60_000;
export const COMMENT_MENTIONS_MAX = 50;
export const COMMENT_BODY_REQUIRED = 'comment.body_required';

export const CommentSchema = z.object({
  id: z.uuid(),
  ticketId: z.uuid(),
  authorId: z.uuid(),
  body: z.string(),
  visibility: CommentVisibilitySchema,
  /** User ids mentioned in the body; drives notifications. */
  mentions: z.array(z.uuid()),
  editedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Comment = z.infer<typeof CommentSchema>;

/**
 * `POST /tickets/{id}/comments` (S-05-08). With `cannedResponseId` the body is the canned
 * text with placeholders resolved and `body` may be omitted; otherwise `body` is required.
 * `visibility: 'internal'` needs `comment.internal`.
 */
export const CreateCommentSchema = z
  .strictObject({
    body: z.string().max(COMMENT_BODY_MAX).optional(),
    visibility: CommentVisibilitySchema.default('public'),
    mentions: z.array(z.uuid()).max(COMMENT_MENTIONS_MAX).default([]),
    cannedResponseId: z.uuid().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.cannedResponseId === undefined && (value.body ?? '').trim().length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: `${COMMENT_BODY_REQUIRED}: body is required unless cannedResponseId is given`,
        path: ['body'],
      });
    }
  });
export type CreateComment = z.infer<typeof CreateCommentSchema>;

/** `PATCH /tickets/{id}/comments/{cid}`: only the text changes; visibility is fixed at creation. */
export const UpdateCommentSchema = z.strictObject({
  body: z.string().min(1).max(COMMENT_BODY_MAX),
});
export type UpdateComment = z.infer<typeof UpdateCommentSchema>;

export const CommentListResponseSchema = z.object({
  items: z.array(CommentSchema),
  nextCursor: z.string().nullable(),
});
export type CommentListResponse = z.infer<typeof CommentListResponseSchema>;
